import { IUser } from "common/utils/UserUtils";
import { prepareSearchParams } from "common/clients/MonarchClient";
import { v4 as uuidv4 } from "uuid";

/**
 *
 * @param endpoint
 * @param successRedirect page to redirect to if login succeeds, relative to index (e.g. "/" for index, "/FooPage" for FooPage)
 * @param failRedirect page to redirect to if login fails, relative to index
 * @param username potentially used to pre-populate login form
 */
export function loginRedirect(
	endpoint: string,
	successRedirect?: string,
	failRedirect?: string,
	username?: string,
	baseUrl?: string
) {
	// Prefer caller-supplied base URL (server-side: derived from x-forwarded-host
	// via viewerBaseUrl). On the client, fall back to window.location.origin so a
	// user on the canonical alias doesn't get bounced to the temp alias baked
	// into process.env.BASE_FE_URL.
	const BASE_FE_URL =
		baseUrl ??
		(typeof window !== "undefined"
			? window.location.origin
			: process.env.BASE_FE_URL);

	const actionURL = `/facade/auth/${endpoint}`;
	const searchParams = prepareSearchParams({
		redirect_success: BASE_FE_URL + (successRedirect || "/"),
		redirect_fail: BASE_FE_URL + (failRedirect || "/"),
		callback_base_url: BASE_FE_URL + "/facade",
		username,
	});
	const fullURL = `${actionURL}?${searchParams.toString()}`;
	if (typeof window !== "undefined") {
		window.location.assign(fullURL);
	} else {
		return fullURL;
	}
}

/**
 * Same as above, but uses logout redirect urls directly, rather than applying the base url
 * @param successRedirect
 * @param failRedirect
 */
export function logoutRedirect(
	successRedirect: string,
	failRedirect: string,
	baseUrl?: string
) {
	const BASE_FE_URL =
		baseUrl ??
		(typeof window !== "undefined"
			? window.location.origin
			: process.env.BASE_FE_URL);

	let successR = `${BASE_FE_URL}${successRedirect}`;
	let failR = `${BASE_FE_URL}${failRedirect}`;

	const actionURL = "/facade/auth/logout";
	const fullURL = `${actionURL}?redirect_success=${encodeURIComponent(
		successR
	)}&redirect_fail=${encodeURIComponent(failR)}`;
	if (typeof window !== "undefined") {
		window.location.assign(fullURL);
	} else {
		return fullURL;
	}
}

export const getLandingPageForNewOrg = (currentPath: string, user: IUser) => {
	const OPERATOR_HOME = "/operator";
	let newPath: string;

	if (
		currentPath.startsWith(OPERATOR_HOME) &&
		user.endorsements.includes("operator:brand")
	) {
		if (
			currentPath.includes("/inventory-data/activities") ||
			currentPath.includes("/emissions-analysis/scope") ||
			currentPath.includes("/renewables")
		) {
			// these should be generic, non-deep-dive pages
			newPath = currentPath;
		} else if (currentPath.includes("/renewable/")) {
			// renewables has a generic list page
			newPath = "/operator/renewables";
		} else if (currentPath.includes("/inventory-data")) {
			// facilities and activities pages all redirect to the activities list
			newPath = "/operator/inventory-data/activities";
		} else {
			newPath = OPERATOR_HOME;
		}
	}
	return (newPath ||= "/");
};

export async function hashEmail(email: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(email.trim().toLowerCase()); // normalize
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function getPersistentClaritySessionId(): string {
	if (typeof window === "undefined") return ""; // server-side safe

	let id = localStorage.getItem("clarity-session-id");
	if (!id) {
		id = uuidv4();
		localStorage.setItem("clarity-session-id", id);
	}
	return id;
}

export const getLocalPartEmail = (email) => {
	return email.split("@")[0];
};
